import { emptySnapshot, type InputSnapshot } from './actions';
import { Keyboard } from './Keyboard';
import { GamepadSource, PadButton } from './GamepadSource';

const MOVE_LEFT = ['KeyA', 'ArrowLeft'] as const;
const MOVE_RIGHT = ['KeyD', 'ArrowRight'] as const;
const MOVE_UP = ['KeyW', 'ArrowUp'] as const;
const MOVE_DOWN = ['KeyS', 'ArrowDown'] as const;
// Enter joins interact (2026-07-27): Steam Input's desktop layout emulates
// the pad as KEYBOARD (A→Enter) — with KeyE alone, that degraded mode could
// navigate menus but never open a chest or descend. Enter is unused in-run.
const INTERACT = ['KeyE', 'Enter'] as const;
const CONFIRM = ['Enter', 'Space', 'KeyE'] as const;
const CANCEL = ['Escape'] as const;
const PAUSE = ['Escape'] as const;
const REROLL = ['KeyR'] as const;
// All four face buttons are taken (A/B/X/Y) — skip rides the left bumper.
// Never overload B/cancel: an Escape reflex would irreversibly burn a token.
const SKIP = ['KeyT'] as const;
const POCKET = ['KeyQ'] as const;
const OVERVIEW = ['Tab', 'KeyI'] as const;
// Space is also CONFIRM — safe: nothing reads confirm in-run, and overlays
// block RunScene, so the two meanings can never fire in the same context.
const ACTIVE = ['KeyF', 'Space'] as const;

// Menu-nav auto-repeat, in 60 Hz ticks. Exported for the input tests.
export const NAV_INITIAL_DELAY = 20; // ~330 ms
export const NAV_REPEAT = 7; // ~115 ms
const NAV_STICK_ON = 0.5; // stick-as-dpad threshold…
const NAV_STICK_OFF = 0.3; // …with hysteresis on release

/**
 * Merges keyboard + gamepad into one InputSnapshot per fixed tick.
 * Edge detection diffs against the previous tick, so a press fires exactly once
 * even when multiple catch-up steps run in one frame.
 */
export class InputManager {
  readonly snapshot: InputSnapshot = emptySnapshot();
  private keyboard = new Keyboard();
  private pad = new GamepadSource();

  private prevInteract = false;
  private prevConfirm = false;
  private prevCancel = false;
  private prevPause = false;
  private prevReroll = false;
  private prevSkip = false;
  private prevPocket = false;
  private prevOverview = false;
  private prevActive = false;

  private navRepeat = { x: new NavAxis(), y: new NavAxis() };
  private device: 'kb' | 'pad' = 'kb';

  attach(target: Window): void {
    this.keyboard.attach(target);
  }

  /** Call once at the top of each sim tick. */
  tick(): void {
    const kb = this.keyboard;
    const pad = this.pad.poll();
    const s = this.snapshot;

    if (kb.activity) {
      this.device = 'kb';
      kb.activity = false;
    }
    if (pad.activity) this.device = 'pad';
    s.device = this.device;

    // Movement: keyboard/d-pad digital + stick analog, summed then clamped.
    let mx = (kb.anyHeld(MOVE_RIGHT) ? 1 : 0) - (kb.anyHeld(MOVE_LEFT) ? 1 : 0);
    let my = (kb.anyHeld(MOVE_DOWN) ? 1 : 0) - (kb.anyHeld(MOVE_UP) ? 1 : 0);
    mx += (pad.buttons[PadButton.DpadRight] ? 1 : 0) - (pad.buttons[PadButton.DpadLeft] ? 1 : 0);
    my += (pad.buttons[PadButton.DpadDown] ? 1 : 0) - (pad.buttons[PadButton.DpadUp] ? 1 : 0);
    mx += pad.stickX;
    my += pad.stickY;
    const mag = Math.hypot(mx, my);
    if (mag > 1) {
      mx /= mag;
      my /= mag;
    }
    s.moveX = mx;
    s.moveY = my;

    // Buttons, merged across devices.
    const interact = kb.anyHeld(INTERACT) || (pad.buttons[PadButton.A] ?? false);
    const confirm = kb.anyHeld(CONFIRM) || (pad.buttons[PadButton.A] ?? false);
    const cancel = kb.anyHeld(CANCEL) || (pad.buttons[PadButton.B] ?? false);
    const pause = kb.anyHeld(PAUSE) || (pad.buttons[PadButton.Start] ?? false);
    const reroll = kb.anyHeld(REROLL) || (pad.buttons[PadButton.X] ?? false);
    const skip = kb.anyHeld(SKIP) || (pad.buttons[PadButton.LB] ?? false);
    const pocket = kb.anyHeld(POCKET) || (pad.buttons[PadButton.Y] ?? false);
    const overview = kb.anyHeld(OVERVIEW) || (pad.buttons[PadButton.Back] ?? false);
    const active = kb.anyHeld(ACTIVE) || (pad.buttons[PadButton.RB] ?? false);

    s.interactHeld = interact;
    s.activeHeld = active;
    s.activePressed = active && !this.prevActive;
    s.interactPressed = interact && !this.prevInteract;
    s.confirmPressed = confirm && !this.prevConfirm;
    s.cancelPressed = cancel && !this.prevCancel;
    s.pausePressed = pause && !this.prevPause;
    s.rerollPressed = reroll && !this.prevReroll;
    s.skipPressed = skip && !this.prevSkip;
    s.pocketPressed = pocket && !this.prevPocket;
    s.overviewPressed = overview && !this.prevOverview;
    this.prevInteract = interact;
    this.prevConfirm = confirm;
    this.prevCancel = cancel;
    this.prevPause = pause;
    this.prevReroll = reroll;
    this.prevSkip = skip;
    this.prevPocket = pocket;
    this.prevOverview = overview;
    this.prevActive = active;

    // Menu nav: dpad + stick-as-dpad (hysteresis) + arrows/WASD, with repeat.
    const navRawX =
      ((pad.buttons[PadButton.DpadRight] ? 1 : 0) - (pad.buttons[PadButton.DpadLeft] ? 1 : 0)) ||
      stickDigital(pad.stickX, this.navRepeat.x.stickActive) ||
      ((kb.anyHeld(MOVE_RIGHT) ? 1 : 0) - (kb.anyHeld(MOVE_LEFT) ? 1 : 0));
    const navRawY =
      ((pad.buttons[PadButton.DpadDown] ? 1 : 0) - (pad.buttons[PadButton.DpadUp] ? 1 : 0)) ||
      stickDigital(pad.stickY, this.navRepeat.y.stickActive) ||
      ((kb.anyHeld(MOVE_DOWN) ? 1 : 0) - (kb.anyHeld(MOVE_UP) ? 1 : 0));

    this.navRepeat.x.stickActive = Math.abs(pad.stickX) > (this.navRepeat.x.stickActive ? NAV_STICK_OFF : NAV_STICK_ON);
    this.navRepeat.y.stickActive = Math.abs(pad.stickY) > (this.navRepeat.y.stickActive ? NAV_STICK_OFF : NAV_STICK_ON);

    s.navX = this.navRepeat.x.tick(navRawX);
    s.navY = this.navRepeat.y.tick(navRawY);
  }
}

function stickDigital(v: number, active: boolean): -1 | 0 | 1 {
  const threshold = active ? NAV_STICK_OFF : NAV_STICK_ON;
  if (v > threshold) return 1;
  if (v < -threshold) return -1;
  return 0;
}

/** Initial-delay-then-repeat so pad and keyboard menus feel identical. */
export class NavAxis {
  stickActive = false;
  private heldDir: -1 | 0 | 1 = 0;
  private heldTicks = 0;

  tick(raw: number): -1 | 0 | 1 {
    const dir: -1 | 0 | 1 = raw > 0 ? 1 : raw < 0 ? -1 : 0;
    if (dir === 0 || dir !== this.heldDir) {
      this.heldDir = dir;
      this.heldTicks = 0;
      return dir;
    }
    this.heldTicks++;
    if (this.heldTicks >= NAV_INITIAL_DELAY &&
        (this.heldTicks - NAV_INITIAL_DELAY) % NAV_REPEAT === 0) {
      return dir;
    }
    return 0;
  }
}
